import {FC, useContext, useEffect, useMemo} from "react";
import {Box, BoxProps, ThemeProvider} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {useApi} from "@buildwitt/shapeshift-identity";
import {ILibraryCourse} from "@buildwitt/shapeshift-types";
import {convertSecondsToHHMMSS} from "../../../utils";
import {AdminHeader} from "../../ManagerView/AdminHeader";
import {DataGridPro, GridColumns} from "@mui/x-data-grid-pro";
import {palette} from "../../../constants"
import {adminTheme as createAdminTheme} from "../../../templates/adminTheme";
import {ICourseModule} from "@buildwitt/shapeshift-types/ICourse";
import {LocaleContext} from "../../../contexts/localeContext";
import {useSSTranslation} from "../../../i18n/useSSTranslation";
import {esES} from "@mui/x-data-grid-pro";

const useStyles = makeStyles()((theme) => ({
	contentBox: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(2.5),
		backgroundColor: palette.greyDark,
		borderWidth: 0,
		borderRadius: theme.borderRadius.small,
		"& .MuiButton-text": {
			color: theme.palette.primary.main,
			border: "0px solid",
			borderRadius: "0",
			fontWeight: "600",
			fontSize: "0.8125rem",
			textTransform: "uppercase",
			padding: "12px 5px 12px",
			letterSpacing: "0.025rem",
			"&:hover": {
				background: "transparent",
				opacity: "0.8"
			},
			"& span": {
				marginRight: 0,
				background: "transparent"
			},
			"& svg": {
				marginRight: "8px",
				"& path": {
					fill: `${theme.palette.primary.main} !important`
				}
			},
			"&:disabled": {
				color: theme.palette.primary.main,
				opacity: 0.5,
				"& svg": {
					"& path": {
						fill: theme.palette.primary.main
					}
				}
			}
		},
		"& [class*='customToolbar']": {
			display: "none !important"
		}
	},
	lessonsAdminTable: {
		padding: 0
	}
}));

interface ICustomProps {
	course?: ILibraryCourse;
}

export type IProps = Omit<BoxProps, "children"> & ICustomProps;

export const LessonsTab:FC<IProps> = ({course, ...baseProps}) => {
	const {classes} = useStyles();
	const {t} = useSSTranslation();
	const [rows, getRows] = useApi<ICourseModule[]>()
	const [{locale}] = useContext(LocaleContext)

	const headCells: GridColumns<ICourseModule> = useMemo(() => ([
		{
			field: "title",
			type: "string",
			flex: 1,
			headerName: t("course_lessons_table_title", "Title"),
			minWidth: 240
		},
		{
			field: "subtitle",
			type: "string",
			flex: 1,
			headerName: t("course_lessons_table_subtitle", "Subtitle")
		},
		{
			field: "videoLength",
			type: "string",
			flex: 1,
			headerName: t("course_lessons_table_duration", "Duration"),
			valueGetter: (e) => e.value ? convertSecondsToHHMMSS(e.value) : ""
		}
	]), [t]);

	const adminTheme = useMemo(() => createAdminTheme(), []);

	const dataGridLocaleText = useMemo(() => {
		const baseLocale = locale === "es-419" ? esES.components.MuiDataGrid.defaultProps.localeText : {};
		return {
			...baseLocale,
			footerTotalRows: t("course_lessons_table_total_rows", "Total Rows:")
		};
	}, [locale, t]);

	useEffect(() => {
		if(course)
			getRows({url: `/api/strapi-plugin-shapeshift-lms/library/courses/${course?.slug}/modules`, data: {locale: locale}})
	}, [course])


	return <Box {...baseProps}>
		<Box className={classes.contentBox}>
			<AdminHeader title={`${course?.title} ${t("course_lessons_title", "Lessons")}`} />
			<ThemeProvider theme={adminTheme}>
				<DataGridPro<ICourseModule>
					className={classes.lessonsAdminTable}
					initialState={{
						sorting: {
							sortModel: [{field: "assignedOn", sort: "desc"}]
						}
					}}
					localeText={dataGridLocaleText}
					aria-label="admin table"
					loading={rows.isLoading}
					keepNonExistentRowsSelected
					rowCount={rows?.data?.length ?? 0}
					rows={rows?.data ?? []}
					columns={headCells}
					checkboxSelection={false}
					autoHeight
				/>
			</ThemeProvider>
		</Box>
	</Box>
}